/**
 * Clickable Tooltip Component
 * Works on both desktop (hover) and mobile (click/touch)
 */

import React, { useState, useEffect, useRef } from 'react';

interface ClickableTooltipProps {
  tooltip: string;
  children: React.ReactNode;
  className?: string;
  onIconClick?: (e: React.MouseEvent) => void;
  /** Click or touch the trigger again to close (no hover-only mode). */
  clickToToggle?: boolean;
  /** Wrap text in a taller box (better on narrow screens). */
  multiline?: boolean;
}

export const ClickableTooltip: React.FC<ClickableTooltipProps> = ({ 
  tooltip, 
  children, 
  className = '',
  onIconClick,
  clickToToggle = false,
  multiline = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const showTooltip = clickToToggle ? isOpen : isHovered || isOpen;

  // Handle animation state
  useEffect(() => {
    if (showTooltip) {
      setShouldRender(true);
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [showTooltip]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Close on scroll
    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current) {
      const updatePosition = () => {
        const tooltip = tooltipRef.current;
        const container = containerRef.current;
        if (!tooltip || !container) return;
        
        // Ensure tooltip is visible (even if opacity 0) to get accurate measurements
        const originalVisibility = tooltip.style.visibility;
        const originalOpacity = tooltip.style.opacity;
        tooltip.style.visibility = 'visible';
        tooltip.style.opacity = '0';
        
        // Force a reflow to ensure dimensions are calculated
        void tooltip.offsetHeight;
        
        const containerRect = container.getBoundingClientRect();
        let tooltipRect = tooltip.getBoundingClientRect();
        
        // If tooltip or container has no dimensions yet, wait a bit and retry
        if ((tooltipRect.width === 0 || tooltipRect.height === 0) || 
            (containerRect.width === 0 && containerRect.height === 0)) {
          requestAnimationFrame(() => {
            updatePosition();
          });
          tooltip.style.visibility = originalVisibility;
          tooltip.style.opacity = originalOpacity;
          return;
        }
        
        // Restore visibility state
        tooltip.style.visibility = originalVisibility;
        tooltip.style.opacity = originalOpacity;
        
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 8;
        const gap = 8;
        
        // Always use absolute positioning relative to the icon
        tooltip.style.position = 'absolute';
        
        // Find the closest parent container that might clip tooltips to get proper bounds
        let bounds = {
          left: padding,
          right: viewportWidth - padding,
          top: padding,
          bottom: viewportHeight - padding,
          width: viewportWidth - (padding * 2),
          height: viewportHeight - (padding * 2)
        };
        
        // Walk up the DOM tree to find containers that might clip tooltips
        let parentContainer = container.parentElement;
        while (parentContainer && parentContainer !== document.body) {
          const computedStyle = window.getComputedStyle(parentContainer);
          const overflow = computedStyle.overflow;
          const overflowY = computedStyle.overflowY;
          const overflowX = computedStyle.overflowX;
          const position = computedStyle.position;
          
          // Check if this container clips content
          const clipsContent = overflow === 'hidden' || overflow === 'auto' || overflow === 'scroll' ||
                               overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll' ||
                               overflowX === 'hidden' || overflowX === 'auto' || overflowX === 'scroll';
          
          // If we find a clipping container, use its bounds
          if (clipsContent && position !== 'static') {
            const parentRect = parentContainer.getBoundingClientRect();
            bounds = {
              left: parentRect.left + padding,
              right: parentRect.right - padding,
              top: parentRect.top + padding,
              bottom: parentRect.bottom - padding,
              width: parentRect.width - (padding * 2),
              height: parentRect.height - (padding * 2)
            };
            break;
          }
          
          parentContainer = parentContainer.parentElement;
        }
        
        // Position relative to container (absolute positioning)
        // Center horizontally - use 50% and translateX
        let top = -tooltipRect.height - gap;
        let arrowTop = '100%';
        let arrowBottom = 'auto';
        let borderTopColor = 'rgba(0, 0, 0, 0.7)';
        let borderBottomColor = 'transparent';
        
        // Calculate where tooltip would appear in viewport
        let tooltipTopInViewport = containerRect.top + top;
        let tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
        
        // Check if tooltip would go outside bounds when positioned above
        const wouldOverflowTop = tooltipTopInViewport < bounds.top;
        const wouldOverflowBottom = tooltipBottomInViewport > bounds.bottom;
        
        // If not enough space above, position below
        if (wouldOverflowTop && !wouldOverflowBottom) {
          top = containerRect.height + gap;
          arrowTop = 'auto';
          arrowBottom = '100%';
          borderTopColor = 'transparent';
          borderBottomColor = 'rgba(0, 0, 0, 0.7)';
          tooltipTopInViewport = containerRect.top + top;
          tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
        }
        
        // Ensure tooltip stays within bounds vertically
        if (tooltipTopInViewport < bounds.top) {
          // Not enough space above, try below
          if (containerRect.bottom + gap + tooltipRect.height <= bounds.bottom) {
            top = containerRect.height + gap;
            arrowTop = 'auto';
            arrowBottom = '100%';
            borderTopColor = 'transparent';
            borderBottomColor = 'rgba(0, 0, 0, 0.7)';
            tooltipTopInViewport = containerRect.top + top;
            tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
          } else {
            // Not enough space either way, position at top of bounds
            top = (bounds.top - containerRect.top) - tooltipRect.height - gap;
            if (top < -containerRect.height) {
              // Still doesn't fit, position at bottom
              top = containerRect.height + gap;
              arrowTop = 'auto';
              arrowBottom = '100%';
              borderTopColor = 'transparent';
              borderBottomColor = 'rgba(0, 0, 0, 0.7)';
            }
            tooltipTopInViewport = containerRect.top + top;
            tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
          }
        } else if (tooltipBottomInViewport > bounds.bottom && top > 0) {
          // Tooltip goes below bounds, try to fit it
          const availableSpace = bounds.bottom - containerRect.top - gap;
          if (availableSpace >= tooltipRect.height) {
            top = availableSpace - tooltipRect.height;
            tooltipTopInViewport = containerRect.top + top;
            tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
          } else {
            // Not enough space below, position above
            top = -tooltipRect.height - gap;
            arrowTop = '100%';
            arrowBottom = 'auto';
            borderTopColor = 'rgba(0, 0, 0, 0.7)';
            borderBottomColor = 'transparent';
            tooltipTopInViewport = containerRect.top + top;
            tooltipBottomInViewport = tooltipTopInViewport + tooltipRect.height;
          }
        }
        
        // Check horizontal overflow and adjust if needed
        const tooltipCenterX = containerRect.left + (containerRect.width / 2);
        const tooltipLeftInViewport = tooltipCenterX - (tooltipRect.width / 2);
        const tooltipRightInViewport = tooltipCenterX + (tooltipRect.width / 2);
        
        let leftOffset = 0;
        if (tooltipLeftInViewport < bounds.left) {
          // Tooltip goes off left edge - shift right
          leftOffset = bounds.left - tooltipLeftInViewport;
        } else if (tooltipRightInViewport > bounds.right) {
          // Tooltip goes off right edge - shift left
          leftOffset = bounds.right - tooltipRightInViewport;
        }
        
        // Apply positioning (absolute relative to container)
        tooltip.style.top = `${top}px`;
        tooltip.style.left = '50%';
        tooltip.style.transform = `translateX(calc(-50% + ${leftOffset}px))`;
        
        // Update arrow position
        const arrow = tooltip.querySelector('.tooltip-arrow') as HTMLElement;
        if (arrow) {
          arrow.style.top = arrowTop;
          arrow.style.bottom = arrowBottom;
          arrow.style.borderTopColor = borderTopColor;
          arrow.style.borderBottomColor = borderBottomColor;
          arrow.style.left = '50%';
          arrow.style.transform = 'translateX(-50%)';
        }
      };
      
      // Update position after render
      requestAnimationFrame(() => {
        updatePosition();
        setTimeout(updatePosition, 10);
      });
      
      // Also update on scroll and resize
      window.addEventListener('scroll', updatePosition, { passive: true });
      window.addEventListener('resize', updatePosition, { passive: true });
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible]);

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (clickToToggle) {
      setIsOpen((open) => !open);
    } else if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      setIsOpen(!isOpen);
    }
    onIconClick?.(e);
  };

  return (
    <span 
      ref={containerRef}
      className={`relative ${className}`}
      onClick={handleIconClick}
      onMouseEnter={() => {
        if (!clickToToggle) setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (!clickToToggle) setIsHovered(false);
      }}
    >
      {children}
      {shouldRender && (
        <span
          ref={tooltipRef}
          className={`absolute text-white pointer-events-none shadow-lg ${
            multiline ? 'whitespace-normal text-left' : 'whitespace-nowrap'
          }`}
          style={{
            zIndex: 9999999,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            opacity: isVisible ? 1 : 0,
            visibility: isVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
            left: '50%',
            transform: 'translateX(-50%)',
            width: multiline ? 'min(13.5rem, calc(100vw - 1.5rem))' : undefined,
            maxWidth: multiline ? 'min(13.5rem, calc(100vw - 1.5rem))' : '90vw',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            padding: multiline ? '10px 12px' : '4px 8px',
            borderRadius: multiline ? '8px' : '4px',
            fontSize: multiline ? '11px' : '10px',
            lineHeight: multiline ? '1.5' : '1.3'
          }}
        >
          {tooltip}
          <span
            className="tooltip-arrow absolute left-1/2 -translate-x-1/2 border-4 border-transparent"
            style={{
              top: '100%',
              borderTopColor: 'rgba(0, 0, 0, 0.7)'
            }}
          />
        </span>
      )}
    </span>
  );
};

